import useWidth from '../../../hooks/useWidth';
import { useCallback, useEffect, useRef } from 'react';
import { VariableSizeList as List, VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import LeaderboardVolumeRow from './LeaderboardVolumeRow';
import Spinner from '../../../components/Spinner';
// import useUserRankings from '../../../hooks/analytics/useUserRankings';
import useMockUserRankings from '../../../hooks/analytics/useMockUserRankings';
import { LeaderboardProps } from '..';
import LeaderboardStatus from '../LeaderboardStatus';
import useTable from '../../../components/Table';
import { SortDirection } from '../../../components/Table/tableTypes';

const LeaderboardVolume: React.FC<LeaderboardProps> = ({ dataPerRow, rowMargin }) => {
    const windowWidth = useWidth();

    // const { userRankings, hasNextPage } = useUserRankings();
    const { userRankings, hasNextPage } = useMockUserRankings();

    const { Table, activeSort } = useTable({
        data: userRankings,
        initialSortKey: 'totalVolume',
    });

    const listRef = useRef<VariableSizeList<any> | null>(null);

    const rankingData = userRankings
        ? [...userRankings].sort((a, b) => {
              const direction = activeSort.direction === SortDirection.ASC ? 1 : -1;

              const aValue = parseFloat(a[activeSort.sortKey].toString());
              const bValue = parseFloat(b[activeSort.sortKey].toString());

              return (aValue - bValue) * direction;
          })
        : [];

    const itemCount = hasNextPage ? rankingData.length + 1 : rankingData.length;

    const isItemLoaded = (index: number) => !hasNextPage || index < rankingData.length;

    const getHeight = useCallback(() => {
        let height;

        switch (true) {
            case windowWidth <= 500:
                height = 209;
                break;
            case windowWidth <= 768:
                height = 153;
                break;
            case windowWidth <= 900:
                height = 75;
                break;
            default:
                height = 97;
                break;
        }

        return height + rowMargin;
    }, [windowWidth, rowMargin]);

    const handleAddMore = async () => {};

    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [windowWidth]);

    return (
        <div className='leaderboard-table leaderboard-volume'>
            {userRankings && userRankings.length ? (
                <Table>
                    <div className='row header'>
                        <Table.Header>Rank</Table.Header>
                        <Table.Header>User</Table.Header>
                        <Table.Header sortKey='wins' withSort>
                            Win
                        </Table.Header>
                        <Table.Header sortKey='losses' withSort>
                            Loss
                        </Table.Header>
                        <Table.Header sortKey='totalMargin' withSort>
                            Collateral
                        </Table.Header>
                        <Table.Header sortKey='totalVolume' withSort>
                            Volume
                        </Table.Header>
                    </div>
                    <InfiniteLoader
                        isItemLoaded={isItemLoaded}
                        itemCount={itemCount}
                        loadMoreItems={handleAddMore}
                    >
                        {({ onItemsRendered, ref }) => (
                            <List
                                height={
                                    getHeight() *
                                    Math.min(userRankings.length, dataPerRow)
                                }
                                itemCount={itemCount}
                                itemSize={getHeight}
                                width={'100%'}
                                className='row-container'
                                ref={(list) => {
                                    ref(list);

                                    listRef.current = list;
                                }}
                                onItemsRendered={onItemsRendered}
                            >
                                {({ index, style }) => {
                                    return !isItemLoaded(index) ? (
                                        <div
                                            className='loading-container'
                                            style={{
                                                ...style,
                                                top: (style.top as number) + 30, // adds margin
                                                height: (style.height as number) + 20, // adds margin
                                            }}
                                        >
                                            <Spinner />
                                            <span>Loading more users...</span>
                                        </div>
                                    ) : (
                                        <LeaderboardVolumeRow
                                            dataPerRow={dataPerRow}
                                            data={{ ...rankingData[index], rank: index }}
                                            key={index}
                                            style={{
                                                ...style,
                                                height:
                                                    (style.height as number) - rowMargin,
                                            }}
                                        />
                                    );
                                }}
                            </List>
                        )}
                    </InfiniteLoader>
                </Table>
            ) : (
                <LeaderboardStatus type='empty-data' />
            )}
        </div>
    );
};

export default LeaderboardVolume;
